import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormBuilder, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator, Validators } from '@angular/forms';
import { I18nService } from '@timechef/core/i18n/i18n.service';
import { TcValidators } from '@timechef/core/utils';

@Component({
  selector: 'app-password',
  templateUrl: './password.component.html',
  styleUrls: ['./password.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: PasswordComponent,
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: PasswordComponent,
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PasswordComponent implements ControlValueAccessor, Validator, OnInit {
  @Input() password?: string;
  @Input() isNewPassword = false;
  @Input() withValidators = false;
  @Input() label = this.i18n.translate('PASSWORD_COMPONENT.PASSWORD');

  showPassword = false;

  passwordControl = this.fb.nonNullable.control<string>('', Validators.required);

  constructor(
    private i18n: I18nService,
    private fb: FormBuilder,
  ) {}

  ngOnInit() {
    if (this.withValidators) {
      this.passwordControl.addValidators([
        // Has a minimum length of 12 characters
        Validators.minLength(12),
        // check whether the entered password has a number
        TcValidators.pattern(/\d/, { hasNumber: true }),
        // check whether the entered password has upper case letter
        TcValidators.pattern(/[A-Z]/, { hasCapitalCase: true }),
        // check whether the entered password has a lower-case letter
        TcValidators.pattern(/[a-z]/, { hasSmallCase: true }),
        // check whether the entered password has a special character (regexp from Timechef v1)
        TcValidators.pattern(/[^a-zA-Z\d]/, { hasSpecialCharacters: true }),
      ]);
      this.passwordControl.updateValueAndValidity();
    }

    this.passwordControl.valueChanges.subscribe((x) => {
      if (this.propagateOnChange) this.propagateOnChange(x);
    });
  }

  togglePassword() {
    this.showPassword = !this.showPassword;
  }

  /** ControlValueAccessor Implementation */

  writeValue(obj: any): void {
    this.passwordControl.setValue(obj);
  }

  propagateOnChange?: (_: any) => void;
  registerOnChange(fn: (_: any) => void): void {
    this.propagateOnChange = fn;
  }

  propagateOnTouched?: () => void;
  registerOnTouched(fn: () => void): void {
    this.propagateOnTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.passwordControl.disable() : this.passwordControl.enable();
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    return this.passwordControl.errors;
  }
}
